import { resolveAbilityId } from "../catalogs/characterSystemCatalog";
import { parseDamageFormula } from "./damageFormula";

export const attackAbilityByWeaponType = {
  melee: "strength",
  ranged: "dexterity",
};

export function getAbilityModifier(characteristics, abilityId) {
  const normalizedAbilityId = resolveAbilityId(abilityId);
  const ability = characteristics?.mainCharacteristicList?.find(
    (item) => resolveAbilityId(item.abilityId || item.name) === normalizedAbilityId
  );
  const score = Number(ability?.diceValue) || 10;
  return Math.floor((score - 10) / 2);
}

export function getWeaponAttack(characteristics, weapon) {
  const abilityId =
    attackAbilityByWeaponType[weapon.weaponType] ?? "strength";
  const abilityModifier = getAbilityModifier(characteristics, abilityId);
  const baseAttackModifier = Number(characteristics?.baseAttackModifier) || 0;
  const weaponModifier = Number(weapon.modifier) || 0;

  return {
    abilityId,
    abilityModifier,
    baseAttackModifier,
    weaponModifier,
    totalModifier: abilityModifier + baseAttackModifier + weaponModifier,
  };
}

const rollFormula = (
  { formula, diceCount, diceSides, bonus },
  random
) => {
  const parsed = parseDamageFormula(formula);
  const count = Number(diceCount ?? parsed.diceCount) || 0;
  const sides = Number(diceSides ?? parsed.diceSides) || 0;
  const constant = Number(bonus ?? parsed.bonus) || 0;

  if (count <= 0 || sides <= 0) {
    return null;
  }

  const rolls = Array.from(
    { length: count },
    () => Math.floor(random() * sides) + 1
  );

  return {
    diceCount: count,
    diceSides: sides,
    bonus: constant,
    rolls,
    total: rolls.reduce((sum, roll) => sum + roll, constant),
  };
};

export function rollWeaponDamage(
  weapon,
  random = Math.random,
  ammunition = null
) {
  const weaponDamage = rollFormula(
    {
      formula: weapon.damage,
      diceCount: weapon.damageDiceCount,
      diceSides: weapon.damageDiceSides,
      bonus: weapon.damageBonus,
    },
    random
  );

  if (!ammunition) {
    return weaponDamage;
  }

  const ammunitionDamage = rollFormula(
    {
      formula: ammunition.damage,
      diceCount: ammunition.damageDiceCount,
      diceSides: ammunition.damageDiceSides,
      bonus: ammunition.damageBonus,
    },
    random
  );

  if (!ammunitionDamage) {
    return weaponDamage;
  }

  return {
    diceCount: weaponDamage?.diceCount ?? 0,
    diceSides: weaponDamage?.diceSides ?? 0,
    bonus: (weaponDamage?.bonus ?? 0) + ammunitionDamage.bonus,
    rolls: weaponDamage?.rolls ?? [],
    total: (weaponDamage?.total ?? 0) + ammunitionDamage.total,
    diceGroups: [
      ...(weaponDamage
        ? [{
            label: "Weapon",
            diceLabel: `${weaponDamage.diceCount}d${weaponDamage.diceSides}`,
            rolls: weaponDamage.rolls,
          }]
        : []),
      {
        label: ammunition.name || "Ammunition",
        diceLabel: `${ammunitionDamage.diceCount}d${ammunitionDamage.diceSides}`,
        rolls: ammunitionDamage.rolls,
      },
    ],
    modifierBreakdown: [
      ...(weaponDamage
        ? [{ label: "Weapon", value: weaponDamage.bonus }]
        : []),
      { label: ammunition.name || "Ammunition", value: ammunitionDamage.bonus },
    ],
  };
}

export function rollWeaponCritical(weapon, random = Math.random) {
  return rollFormula(
    {
      formula: weapon.crit,
      diceCount: weapon.critDiceCount,
      diceSides: weapon.critDiceSides,
      bonus: weapon.critBonus,
    },
    random
  );
}
